import React from "react";
import { useSearchParams } from "react-router";
import i18n from "src/i18n/config";

import { showErrorToast } from "@shared/toasts/Toasts";

type PolicyId = string | null;
type UseCurrentPolicy = (policies: API.PolicyViewModel[]) => [PolicyId, (value: PolicyId) => void];

export const policyNameExists = (policies: API.PolicyViewModel[], name: string): boolean =>
    policies.some((policy) => policy.name?.trim().toLowerCase() === name.trim().toLowerCase());

export const useCurrentPolicy: UseCurrentPolicy = (policies) => {
    const [searchParams, setSearchParams] = useSearchParams();
    const paramPolicyId = searchParams.get("policyId");
    const isParamPolicyReachable = Boolean(paramPolicyId) && policies.some((policy) => policy.id === paramPolicyId);

    const [id, setId] = React.useState<PolicyId>(isParamPolicyReachable ? paramPolicyId : (policies[0]?.id ?? null));

    const setCurrentPolicyId = (value: PolicyId) => {
        setId(value);
        setSearchParams(value ? { policyId: value } : {}, { replace: true });
    };

    React.useEffect(() => {
        if (!paramPolicyId || isParamPolicyReachable) {
            return;
        }

        showErrorToast(i18n.t("pages:policiesPage.toast.dontHaveAccessToPolicy"));
        const fallbackPolicyId = policies[0]?.id ?? null;
        setSearchParams(fallbackPolicyId ? { policyId: fallbackPolicyId } : {}, { replace: true });
        // eslint-disable-next-line react-hooks/exhaustive-deps -- the `policyId` from the URL is checked once, on entry
    }, []);

    return [id, setCurrentPolicyId];
};
